import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Save, Download, Crown, X, FileText } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { Pricing } from "@/components/Pricing";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from "docx";
import jsPDF from "jspdf";
import { soundManager } from "@/utils/sounds";
import { generateResumeWithTemplate } from "@/lib/templateGenerators";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { queueSavedResume } from "@/lib/offlineResumeQueue";

interface ResumeActionsProps {
  resumeData: any;
  isConversationComplete?: boolean;
  selectedTemplateId?: string | null;
  onSave?: () => void;
}

function cleanDownloadText(value: any): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.replace(/\s+/g, ' ').trim();
  if (typeof value === 'number') return String(value);
  return '';
}

function isReferencePlaceholder(value: string): boolean {
  return /\b(references?|referees?)\b.*\b(available|upon request)\b/i.test(value) ||
    /\bavailable upon request\b/i.test(value);
}

function normalizeReferencesForDownload(value: any): string[] {
  const source = Array.isArray(value) ? value : value ? [value] : [];

  return source
    .flatMap((item) => {
      if (!item) return [];
      if (typeof item === 'string') {
        return item
          .split(/\r?\n|[;|]/g)
          .map((part) => part.replace(/^[-*\u2022]\s*/, '').trim())
          .filter(Boolean);
      }
      return [item];
    })
    .map((item) => {
      if (typeof item !== 'object') return cleanDownloadText(item);

      const name = cleanDownloadText(item.name || item.fullName || item.refereeName || item.referenceName);
      const title = [
        cleanDownloadText(item.position || item.title || item.role || item.relationship),
        cleanDownloadText(item.company || item.organization),
      ].filter(Boolean).join(', ');
      const contact = [
        cleanDownloadText(item.email),
        cleanDownloadText(item.phone || item.mobile),
        cleanDownloadText(item.linkedin || item.website),
      ].filter(Boolean).join(' | ');

      return [name, title, contact].filter(Boolean).join(' | ');
    })
    .filter((item) => item && !isReferencePlaceholder(item));
}

function getEffectiveTemplateId(resumeData: any, selectedTemplateId?: string | null): string {
  const templateId =
    selectedTemplateId ||
    resumeData?.selectedTemplateId ||
    resumeData?.templateId ||
    resumeData?.template?.id ||
    resumeData?.formatting?.templateId;

  return typeof templateId === 'string' && templateId.trim()
    ? templateId.trim()
    : 'professional-classic';
}

function getDownloadTemplateId(templateId: string): string {
  return templateId === 'keep-original' ? 'professional-classic' : templateId;
}

function withTemplateMetadata(resumeData: any, templateId: string) {
  return {
    ...(resumeData || {}),
    selectedTemplateId: templateId,
    templateId,
  };
}

export function ResumeActions({ resumeData, isConversationComplete = false, selectedTemplateId, onSave }: ResumeActionsProps) {
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showPricingDialog, setShowPricingDialog] = useState(false);
  const [showFormatDialog, setShowFormatDialog] = useState(false);
  const [showPostDownloadPopup, setShowPostDownloadPopup] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [highlightSaveButton, setHighlightSaveButton] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const isOnline = useOnlineStatus();
  const [popupPosition, setPopupPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 }); // post-download popup position(under the improve button)


  const checkUserSubscription = async () => {
    if (!user) return false;

    const { data } = await supabase
      .from('profiles')
      .select('subscription_tier')
      .eq('user_id', user.id)
      .single();

    setUserProfile(data);
    return data?.subscription_tier === 'pro';
  };

  const handleSaveResume = async () => {
    if (!user) return;

    const templateId = getEffectiveTemplateId(resumeData, selectedTemplateId);
    const title = `Resume - ${new Date().toLocaleDateString()}`;
    const content = withTemplateMetadata(resumeData, templateId);

    if (!isOnline) {
      setLoading(true);
      try {
        await queueSavedResume({
          userId: user.id,
          title,
          content,
          templateId,
        });

        soundManager.save();
        toast({
          title: "Resume queued offline",
          description: "Your resume is saved locally and will sync when you reconnect.",
        });
        onSave?.();
      } catch (error) {
        console.error("Failed to queue offline resume:", error);
        soundManager.error();
        toast({
          title: "Offline save failed",
          description: "Please download your resume now or try saving again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    const canSave = await checkUserSubscription();

    if (!canSave) {
      setShowUpgradeDialog(true);
      return;
    }

    setLoading(true);
    const { error } = await supabase
      .from('saved_resumes')
      .insert({
        user_id: user.id,
        title,
        content,
      });

    if (error) {
      soundManager.error();
      toast({
        title: "Error",
        description: "Failed to save resume. Please try again.",
        variant: "destructive",
      });
    } else {
      soundManager.save();
      toast({
        title: "Resume Saved!",
        description: "Your resume has been saved successfully.",
      });
      onSave?.();
    }
    setLoading(false);
  };

  const generateWordDocument = async () => {
    // Validate resume data before generation
    if (!resumeData || Object.keys(resumeData).length === 0) {
      throw new Error('No resume data available for document generation');
    }

    // Build full name from components or use existing fullName
    const firstName = resumeData?.personalInfo?.firstName || '';
    const lastName = resumeData?.personalInfo?.lastName || '';
    const fullName = resumeData?.personalInfo?.fullName ||
      (firstName && lastName ? `${firstName} ${lastName}`.trim() : 'Your Name');

    if (!fullName || fullName === 'Your Name') {
      console.warn('Missing or invalid name in resume data:', resumeData);
    }

    const fileName = fullName.replace(/\s+/g, "_");

    // Get contact info with fallbacks
    const email = resumeData?.personalInfo?.email || '';
    const location = resumeData?.personalInfo?.location || resumeData?.personalInfo?.city || '';
    const phone = resumeData?.personalInfo?.mobile || resumeData?.personalInfo?.phone || '';
    const jobTitle = resumeData?.personalInfo?.jobTitle || resumeData?.personalInfo?.title || '';

    // Build contact line only with available info
    const contactParts = [email, location, phone].filter(Boolean);
    const contactLine = contactParts.join(' • ');

    // Get summary
    const summary = resumeData?.summary || '';

    // Get work experience (handle both naming conventions)
    const experiences = resumeData?.workExperience || resumeData?.experience || [];

    // Get education
    const educationList = resumeData?.education || [];

    // Get skills
    const skills = resumeData?.skills || [];

    // Get languages
    const languages = resumeData?.languages || [];

    // Get certifications
    const certifications = resumeData?.certifications || [];

    // Get references/referees when the user provided them or they were parsed from an upload
    const references = normalizeReferencesForDownload(resumeData?.references || resumeData?.referees);

    // Build experience paragraphs
    const experienceParagraphs: Paragraph[] = [];
    experiences.forEach((exp: any) => {
      const companyName = exp.company || exp.companyName || '';
      const position = exp.position || exp.title || exp.jobTitle || '';
      const startYear = exp.startYear || exp.startDate || '';
      const endYear = exp.endYear || exp.endDate || 'Present';
      const expLocation = exp.location || '';
      const descriptions = exp.description || exp.responsibilities || exp.duties || [];

      if (companyName) {
        experienceParagraphs.push(
          new Paragraph({
            children: [new TextRun({ text: companyName, bold: true, size: 24 })],
            spacing: { before: 200 },
          })
        );
      }

      if (position) {
        experienceParagraphs.push(
          new Paragraph({
            children: [new TextRun({ text: position, italics: true, size: 22 })],
          })
        );
      }

      const dateLocation = [
        startYear && endYear ? `${startYear} - ${endYear}` : '',
        expLocation
      ].filter(Boolean).join(' | ');

      if (dateLocation) {
        experienceParagraphs.push(
          new Paragraph({
            children: [new TextRun({ text: dateLocation, size: 20, color: "666666" })],
          })
        );
      }

      // Add bullet points for descriptions
      const descArray = Array.isArray(descriptions) ? descriptions : [descriptions].filter(Boolean);
      descArray.forEach((desc: string) => {
        if (desc) {
          experienceParagraphs.push(
            new Paragraph({
              children: [new TextRun({ text: `• ${desc}`, size: 20 })],
              indent: { left: 360 },
            })
          );
        }
      });
    });

    // Build education paragraphs
    const educationParagraphs: Paragraph[] = [];
    educationList.forEach((edu: any) => {
      const institution = edu.institution || edu.school || edu.university || '';
      const degree = edu.degree || '';
      const major = edu.major || edu.field || '';
      const startYear = edu.startYear || edu.startDate || '';
      const endYear = edu.endYear || edu.endDate || '';
      const eduLocation = edu.location || '';
      const grade = edu.grade || edu.gpa || '';

      if (institution) {
        educationParagraphs.push(
          new Paragraph({
            children: [new TextRun({ text: institution, bold: true, size: 24 })],
            spacing: { before: 200 },
          })
        );
      }

      const degreeText = [degree, major].filter(Boolean).join(' in ');
      if (degreeText) {
        educationParagraphs.push(
          new Paragraph({
            children: [new TextRun({ text: degreeText, italics: true, size: 22 })],
          })
        );
      }

      const eduDateLocation = [
        startYear && endYear ? `${startYear} - ${endYear}` : '',
        eduLocation
      ].filter(Boolean).join(' | ');

      if (eduDateLocation) {
        educationParagraphs.push(
          new Paragraph({
            children: [new TextRun({ text: eduDateLocation, size: 20, color: "666666" })],
          })
        );
      }

      if (grade) {
        educationParagraphs.push(
          new Paragraph({
            children: [new TextRun({ text: `Grade: ${grade}`, size: 20 })],
          })
        );
      }
    });

    // Build the document
    const docChildren: Paragraph[] = [
      // Header with name
      new Paragraph({
        children: [
          new TextRun({
            text: fullName.toUpperCase(),
            bold: true,
            size: 36,
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 100 },
      }),
    ];

    // Add job title if available
    if (jobTitle) {
      docChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: jobTitle,
              size: 24,
            }),
          ],
          alignment: AlignmentType.CENTER,
        })
      );
    }

    // Add contact line if available
    if (contactLine) {
      docChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: contactLine,
              size: 20,
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 300 },
        })
      );
    }

    // Add summary if available
    if (summary) {
      docChildren.push(
        new Paragraph({
          children: [new TextRun({ text: "SUMMARY", bold: true, size: 26 })],
          spacing: { before: 300, after: 100 },
          border: { bottom: { color: "000000", size: 1, style: "single" } },
        }),
        new Paragraph({
          children: [new TextRun({ text: summary, size: 20 })],
          spacing: { after: 200 },
        })
      );
    }

    // Add experience section if available
    if (experiences.length > 0) {
      docChildren.push(
        new Paragraph({
          children: [new TextRun({ text: "PROFESSIONAL EXPERIENCE", bold: true, size: 26 })],
          spacing: { before: 300, after: 100 },
          border: { bottom: { color: "000000", size: 1, style: "single" } },
        }),
        ...experienceParagraphs
      );
    }

    // Add education section if available
    if (educationList.length > 0) {
      docChildren.push(
        new Paragraph({
          children: [new TextRun({ text: "EDUCATION", bold: true, size: 26 })],
          spacing: { before: 300, after: 100 },
          border: { bottom: { color: "000000", size: 1, style: "single" } },
        }),
        ...educationParagraphs
      );
    }

    // Add skills section if available
    if (skills.length > 0) {
      docChildren.push(
        new Paragraph({
          children: [new TextRun({ text: "SKILLS", bold: true, size: 26 })],
          spacing: { before: 300, after: 100 },
          border: { bottom: { color: "000000", size: 1, style: "single" } },
        }),
        new Paragraph({
          children: [new TextRun({ text: skills.join(', '), size: 20 })],
        })
      );
    }

    // Add languages if available
    if (languages.length > 0) {
      docChildren.push(
        new Paragraph({
          children: [new TextRun({ text: "LANGUAGES", bold: true, size: 26 })],
          spacing: { before: 300, after: 100 },
          border: { bottom: { color: "000000", size: 1, style: "single" } },
        }),
        new Paragraph({
          children: [new TextRun({ text: languages.join(', '), size: 20 })],
        })
      );
    }

    // Add certifications if available
    if (certifications.length > 0) {
      docChildren.push(
        new Paragraph({
          children: [new TextRun({ text: "CERTIFICATIONS", bold: true, size: 26 })],
          spacing: { before: 300, after: 100 },
          border: { bottom: { color: "000000", size: 1, style: "single" } },
        }),
        ...certifications.map((cert: string) =>
          new Paragraph({
            children: [new TextRun({ text: `• ${cert}`, size: 20 })],
          })
        )
      );
    }

    // Add references only when actual references were supplied
    if (references.length > 0) {
      docChildren.push(
        new Paragraph({
          children: [new TextRun({ text: "REFERENCES", bold: true, size: 26 })],
          spacing: { before: 300, after: 100 },
          border: { bottom: { color: "000000", size: 1, style: "single" } },
        }),
        ...references.map((reference: string) =>
          new Paragraph({
            children: [new TextRun({ text: reference, size: 20 })],
            spacing: { after: 80 },
          })
        )
      );
    }

    const doc = new Document({
      sections: [{
        properties: {
          page: {
            margin: { top: 720, right: 720, bottom: 720, left: 720 },
          },
        },
        children: docChildren,
      }],
    });

    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName}_Resume.docx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Validate resume data for binary/PDF corruption
  const validateResumeDataForPDF = (data: any): void => {
    const binaryMarkers = ['%PDF-', 'stream', 'endobj', 'endstream'];
    const jsonStr = JSON.stringify(data);
    
    for (const marker of binaryMarkers) {
      if (jsonStr.includes(marker)) {
        // Check if there's also non-printable content (indicating binary)
        const hasNonPrintable = /[^\x20-\x7E\x0A\x0D\t]/.test(jsonStr);
        if (hasNonPrintable) {
          throw new Error(
            'Resume data appears corrupted with binary content. ' +
            'This usually happens when PDF extraction fails. ' +
            'Please try re-uploading the file or manually enter your information.'
          );
        }
      }
    }
  };

  const generatePDF = () => {
    // Validate resume data before generation
    if (!resumeData || Object.keys(resumeData).length === 0) {
      throw new Error('No resume data available for document generation');
    }

    try {
      validateResumeDataForPDF(resumeData);
    } catch (validationErr) {
      console.error('Resume validation failed:', validationErr);
      throw validationErr;
    }

    // Build full name from components or use existing fullName
    const firstName = resumeData?.personalInfo?.firstName || '';
    const lastName = resumeData?.personalInfo?.lastName || '';
    const fullName = resumeData?.personalInfo?.fullName ||
      (firstName && lastName ? `${firstName} ${lastName}`.trim() : 'Your Name');

    if (!fullName || fullName === 'Your Name') {
      console.warn('Missing or invalid name in resume data:', resumeData);
    }

    const fileName = fullName.replace(/\s+/g, "_");

    // Get contact info with fallbacks
    const email = resumeData?.personalInfo?.email || '';
    const location = resumeData?.personalInfo?.location || resumeData?.personalInfo?.city || '';
    const phone = resumeData?.personalInfo?.mobile || resumeData?.personalInfo?.phone || '';
    const jobTitle = resumeData?.personalInfo?.jobTitle || resumeData?.personalInfo?.title || '';

    // Build contact line only with available info
    const contactParts = [email, location, phone].filter(Boolean);
    const contactLine = contactParts.join(' • ');

    // Get summary
    const summary = resumeData?.summary || '';

    // Get work experience (handle both naming conventions)
    const experiences = resumeData?.workExperience || resumeData?.experience || [];

    // Get education
    const educationList = resumeData?.education || [];

    // Get skills
    const skills = resumeData?.skills || [];

    // Get languages
    const languages = resumeData?.languages || [];

    // Get certifications
    const certifications = resumeData?.certifications || [];

    // Get references/referees when the user provided them or they were parsed from an upload
    const references = normalizeReferencesForDownload(resumeData?.references || resumeData?.referees);

    const doc = new jsPDF();
    let yPosition = 25;
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);

    // Helper to check and add new page
    const checkNewPage = (neededSpace: number = 30) => {
      if (yPosition > 270 - neededSpace) {
        doc.addPage();
        yPosition = 25;
      }
    };

    // Header - Name
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text(fullName.toUpperCase(), pageWidth / 2, yPosition, { align: "center" });
    yPosition += 8;

    // Job Title
    if (jobTitle) {
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(jobTitle, pageWidth / 2, yPosition, { align: "center" });
      yPosition += 7;
    }

    // Contact Line
    if (contactLine) {
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(contactLine, pageWidth / 2, yPosition, { align: "center" });
      yPosition += 12;
    }

    // Summary Section
    if (summary) {
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("SUMMARY", margin, yPosition);
      yPosition += 2;
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 6;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      const summaryLines = doc.splitTextToSize(summary, contentWidth);
      doc.text(summaryLines, margin, yPosition);
      yPosition += summaryLines.length * 5 + 8;
    }

    // Professional Experience Section
    if (experiences.length > 0) {
      checkNewPage(40);
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("PROFESSIONAL EXPERIENCE", margin, yPosition);
      yPosition += 2;
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 8;

      experiences.forEach((exp: any) => {
        checkNewPage(30);

        const companyName = exp.company || exp.companyName || '';
        const position = exp.position || exp.title || exp.jobTitle || '';
        const startYear = exp.startYear || exp.startDate || '';
        const endYear = exp.endYear || exp.endDate || 'Present';
        const expLocation = exp.location || '';
        const descriptions = exp.description || exp.responsibilities || exp.duties || [];

        // Company name
        if (companyName) {
          doc.setFontSize(11);
          doc.setFont("helvetica", "bold");
          doc.text(companyName, margin, yPosition);
          yPosition += 5;
        }

        // Position
        if (position) {
          doc.setFontSize(10);
          doc.setFont("helvetica", "italic");
          doc.text(position, margin, yPosition);
          yPosition += 5;
        }

        // Date and location
        const dateLocation = [
          startYear && endYear ? `${startYear} - ${endYear}` : '',
          expLocation
        ].filter(Boolean).join(' | ');

        if (dateLocation) {
          doc.setFontSize(9);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(100);
          doc.text(dateLocation, margin, yPosition);
          doc.setTextColor(0);
          yPosition += 5;
        }

        // Descriptions/responsibilities
        const descArray = Array.isArray(descriptions) ? descriptions : [descriptions].filter(Boolean);
        descArray.forEach((desc: string) => {
          if (desc) {
            checkNewPage(15);
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            const descLines = doc.splitTextToSize(`• ${desc}`, contentWidth - 10);
            doc.text(descLines, margin + 5, yPosition);
            yPosition += descLines.length * 4 + 2;
          }
        });

        yPosition += 5;
      });
    }

    // Education Section
    if (educationList.length > 0) {
      checkNewPage(40);
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("EDUCATION", margin, yPosition);
      yPosition += 2;
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 8;

      educationList.forEach((edu: any) => {
        checkNewPage(25);

        const institution = edu.institution || edu.school || edu.university || '';
        const degree = edu.degree || '';
        const major = edu.major || edu.field || '';
        const startYear = edu.startYear || edu.startDate || '';
        const endYear = edu.endYear || edu.endDate || '';
        const eduLocation = edu.location || '';
        const grade = edu.grade || edu.gpa || '';

        // Institution
        if (institution) {
          doc.setFontSize(11);
          doc.setFont("helvetica", "bold");
          doc.text(institution, margin, yPosition);
          yPosition += 5;
        }

        // Degree
        const degreeText = [degree, major].filter(Boolean).join(' in ');
        if (degreeText) {
          doc.setFontSize(10);
          doc.setFont("helvetica", "italic");
          doc.text(degreeText, margin, yPosition);
          yPosition += 5;
        }

        // Date and location
        const eduDateLocation = [
          startYear && endYear ? `${startYear} - ${endYear}` : '',
          eduLocation
        ].filter(Boolean).join(' | ');

        if (eduDateLocation) {
          doc.setFontSize(9);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(100);
          doc.text(eduDateLocation, margin, yPosition);
          doc.setTextColor(0);
          yPosition += 5;
        }

        if (grade) {
          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          doc.text(`Grade: ${grade}`, margin, yPosition);
          yPosition += 5;
        }

        yPosition += 3;
      });
    }

    // Skills Section
    if (skills.length > 0) {
      checkNewPage(25);
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("SKILLS", margin, yPosition);
      yPosition += 2;
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 6;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      const skillsText = skills.join(', ');
      const skillsLines = doc.splitTextToSize(skillsText, contentWidth);
      doc.text(skillsLines, margin, yPosition);
      yPosition += skillsLines.length * 5 + 5;
    }

    // Languages Section
    if (languages.length > 0) {
      checkNewPage(25);
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("LANGUAGES", margin, yPosition);
      yPosition += 2;
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 6;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(languages.join(', '), margin, yPosition);
      yPosition += 8;
    }

    // Certifications Section
    if (certifications.length > 0) {
      checkNewPage(25);
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("CERTIFICATIONS", margin, yPosition);
      yPosition += 2;
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 6;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      certifications.forEach((cert: string) => {
        checkNewPage(10);
        doc.text(`• ${cert}`, margin, yPosition);
        yPosition += 5;
      });
    }

    // References Section
    if (references.length > 0) {
      checkNewPage(25);
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("REFERENCES", margin, yPosition);
      yPosition += 2;
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 6;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      references.forEach((reference: string) => {
        checkNewPage(12);
        const referenceLines = doc.splitTextToSize(reference, contentWidth);
        doc.text(referenceLines, margin, yPosition);
        yPosition += referenceLines.length * 5 + 3;
      });
    }

    doc.save(`${fileName}_Resume.pdf`);
  };

  const handleDownloadResume = () => {
    soundManager.signIn();
    setShowFormatDialog(true);
  };

  const handleFormatSelection = async (format: 'pdf' | 'docx') => {
    setShowFormatDialog(false);
    setLoading(true);

    // Validate resume data before proceeding
    if (!resumeData || Object.keys(resumeData).length === 0) {
      soundManager.error();
      toast({
        title: "No Resume Data",
        description: "No resume data available. Please create or load a resume first.",
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    console.log('Starting download with format:', format);
    console.log('Resume data:', resumeData);
    console.log('Template ID:', selectedTemplateId);

    try {
      const templateId = getEffectiveTemplateId(resumeData, selectedTemplateId);
      const downloadTemplateId = getDownloadTemplateId(templateId);
      const resumeForDownload = withTemplateMetadata(resumeData, templateId);

      console.log('Using template generator:', downloadTemplateId);
      await generateResumeWithTemplate(downloadTemplateId, format, resumeForDownload);

      soundManager.signIn();
      toast({
        title: "Download Started",
        description: `Your resume is being downloaded as ${format.toUpperCase()}.`,
      });

      // Show post-download message and highlight save button after a short delay
      setTimeout(() => {
        setShowPostDownloadPopup(true);
        setHighlightSaveButton(true);
        const sidebarImproveButton = document.querySelector('.sidebar-improve-button');
        if (sidebarImproveButton) {
          sidebarImproveButton.classList.add('tour-highlight');
        }
        const rect = sidebarImproveButton.getBoundingClientRect();
        setPopupPosition({
          top: rect.bottom + 10, // 10px below the button
          left: rect.left,       // align with the button's left edge
        });
        // Auto-hide after 10 seconds
        setTimeout(() => {
          setShowPostDownloadPopup(false);
          setHighlightSaveButton(false);
          if (sidebarImproveButton) {
            sidebarImproveButton.classList.remove('tour-highlight');
          }
        }, 10000);
      }, 1000);
    } catch (error) {
      console.error('Download error:', error);
      soundManager.error();
      toast({
        title: "Download Failed",
        description: `There was an error generating your resume: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeClick = () => {
    setShowUpgradeDialog(false);
    setShowPricingDialog(true);
  };

  return (
    <>
      <div className="flex gap-2 p-2 border-t border-border bg-card/80 sm:p-3">
        <Button
          onClick={handleSaveResume}
          disabled={loading || !isConversationComplete}
          className={`h-10 min-w-0 flex-1 px-2 text-xs transition-all duration-300 sm:text-sm ${
            highlightSaveButton
              ? 'ring-2 ring-primary ring-offset-2 ring-offset-background shadow-lg shadow-primary/25'
              : ''
          }`}
        >
          <Save className="h-4 w-4 mr-1.5 shrink-0" />
          <span className="truncate">{loading ? "Saving..." : "Save Resume"}</span>
        </Button>
        <Button
          onClick={handleDownloadResume}
          variant="outline"
          className="h-10 min-w-0 flex-1 px-2 text-xs sm:text-sm"
          disabled={!isConversationComplete}
        >
          <Download className="h-4 w-4 mr-1.5 shrink-0" />
          <span className="truncate">Download Resume</span>
        </Button>
      </div>

      {/* Upgrade Required Dialog */}
      <AlertDialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-yellow-500" />
              Upgrade Required
            </AlertDialogTitle>
            <AlertDialogDescription>
              Unfortunately, you cannot access this feature with your current plan.
              Upgrade to Pro to save unlimited resumes and access them anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowUpgradeDialog(false)}
              className="flex-1"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleUpgradeClick}
              className="flex-1"
            >
              <Crown className="h-4 w-4 mr-2" />
              Upgrade
            </Button>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Format Selection Dialog */}
      <Dialog open={showFormatDialog} onOpenChange={setShowFormatDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Choose Download Format
            </DialogTitle>
            <DialogDescription>
              Select your preferred format to download your resume
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-4">
            <Button
              onClick={() => handleFormatSelection('pdf')}
              variant="outline"
              className="flex items-center justify-center gap-2 h-12"
            >
              <FileText className="h-5 w-5" />
              <div className="text-left">
                <div className="font-medium">PDF Format</div>
                <div className="text-sm text-muted-foreground">Universal compatibility</div>
              </div>
            </Button>
            <Button
              onClick={() => handleFormatSelection('docx')}
              variant="outline"
              className="flex items-center justify-center gap-2 h-12"
            >
              <FileText className="h-5 w-5" />
              <div className="text-left">
                <div className="font-medium">Word Format</div>
                <div className="text-sm text-muted-foreground">Editable document</div>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pricing Dialog */}
      <Dialog open={showPricingDialog} onOpenChange={setShowPricingDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Choose Your Plan</DialogTitle>
            <DialogDescription>
              Upgrade to unlock saved resumes and more features
            </DialogDescription>
          </DialogHeader>
          <Pricing onPaymentStart={() => setShowPricingDialog(false)} />
        </DialogContent>
      </Dialog>

      {/* Post-Download Popup: Help Us Improve */}
      {showPostDownloadPopup && (
        <>
          {/* Background blur overlay */}
          <div className="fixed inset-0 bg-black/50 z-40 pointer-events-none" />

          {/* Popup message */}
          <div className="fixed z-50 max-w-xs" style={{ top: `${popupPosition.top}px`, left: `${popupPosition.left}px`}} >
            <div className="bg-card/95 backdrop-blur-sm border border-border rounded-lg shadow-lg p-4 animate-in slide-in-from-left duration-300">
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-semibold text-sm text-foreground">Your feedback is valuable! Click "Help Us Improve" above to share your thoughts or request a feature.</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowPostDownloadPopup(false);
                    setHighlightSaveButton(false);
                  }}
                  className="h-4 w-4 p-0 text-muted-foreground hover:text-foreground"
                >
                  ✕
                </Button>
              </div>
            </div>
          </div>
          <style>{`
        .tour-highlight {
          position: relative;
          z-index: 45;
          box-shadow: 0 0 0 4px hsl(var(--primary)), 0 0 0 9999px rgba(0, 0, 0, 0.5);
          border-radius: 8px;
        }
      `}</style>
        </>
      )}
    </>
  );
}
